import React, { memo, useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BsFillTvFill, BsMusicPlayerFill } from 'react-icons/bs';
import { FaBookOpenReader } from 'react-icons/fa6';
import { X } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { useAuthModal } from '@/hooks/use-auth-modal';
import { AuthModal } from '@/components/AuthModal';
import { useSubscriptionStatus, useCreateSubscriptionCheckout } from '@/hooks/use-subscription-status';
import { useAppConfig } from '@/hooks/use-app-config';
import { useCurrencyDetection } from '@/hooks/use-currency-detection';
import { useDeviceDetection } from '@/hooks/use-device-detection';
import { useClickSound } from '@/hooks/useClickSound';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ensureApplePay, tryApplePay } from '@/lib/applePay';
import { Button } from '@/components/ui/button';
interface MobileBottomNavProps {
  isNowPlayingOpen: boolean;
  showTrialCTA: boolean;
  setShowTrialCTA: (show: boolean) => void;
  hasAnimatedTrialCTA: boolean;
  isScrolled?: boolean;
}

const MobileBottomNavInner = ({
  isNowPlayingOpen,
  showTrialCTA,
  setShowTrialCTA,
  hasAnimatedTrialCTA,
  isScrolled = false,
}: MobileBottomNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const { isAuthModalOpen, openAuthModal, closeAuthModal } = useAuthModal();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { data: appConfig } = useAppConfig();
  const createCheckout = useCreateSubscriptionCheckout();
  const { country, currency } = useCurrencyDetection();
  const { isIOS } = useDeviceDetection();
  const queryClient = useQueryClient();
  const { play: playClickSound } = useClickSound({ volume: 0.5 });

  const currentPath = location.pathname;
  const [ctaDismissed, setCtaDismissed] = useState(false);

  // Auto-dismiss CTA when user becomes subscribed - prevents nav from staying hidden
  useEffect(() => {
    if (subscriptionStatus?.subscribed) {
      setCtaDismissed(true);
    }
  }, [subscriptionStatus?.subscribed]);

  const navItems = [
    { path: '/', label: 'Buxbox', icon: BsFillTvFill },
    { 
      path: '/my-bundles', 
      label: 'Bux', 
      icon: FaBookOpenReader,
      onClick: () => {
        if (!session) {
          openAuthModal('Please sign in to view your books');
        } else {
          navigate('/my-bundles');
        }
      }
    },
    { 
      path: '/jukebox', 
      label: 'Jukebox', 
      icon: BsMusicPlayerFill,
      onClick: () => {
        if (!session) {
          openAuthModal('Please sign in to access the Jukebox');
        } else {
          navigate('/jukebox');
        }
      }
    }
  ];

  // Get active index for sliding indicator
  const activeIndex = navItems.findIndex(item => item.path === currentPath);

  // Refs for measuring button positions
  const navContainerRef = useRef<HTMLElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const navRef = useRef<HTMLDivElement>(null);
  
  // Indicator state - stored position, only updated on click or resize
  // Initialize with sensible defaults to prevent invisible nav on cold start
  const [indicatorStyle, setIndicatorStyle] = useState({ 
    left: 0, 
    width: 80, // Default width as fallback
    opacity: 1  // Start visible to prevent flash
  });
  
  // Track previous index to determine animation direction
  const prevActiveIndex = useRef(activeIndex);
  
  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef<number | null>(null);
  
  // Flag: only animate when user clicks a tab
  const shouldAnimateNext = useRef(false);
  
  // Flag: indicator has been initialized
  const initialized = useRef(false);
  
  // Retry counter for measurement failures
  const measureRetryCount = useRef(0);
  const MAX_MEASURE_RETRIES = 5;

  // Main tab paths that should NOT scroll to top
  const mainTabPaths = ['/', '/my-bundles', '/search', '/jukebox'];

  // Measure a button's position relative to container - with fallback
  const measureButton = useCallback((index: number): { left: number; width: number } | null => {
    if (index < 0 || index >= navItems.length) return null;
    
    const button = buttonRefs.current[index];
    const container = navContainerRef.current;
    
    if (!button || !container) {
      // Return null but don't crash - caller handles fallback
      return null;
    }
    
    try {
      const buttonRect = button.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      // Validate measurements - sometimes getBoundingClientRect returns 0s on first render
      if (buttonRect.width === 0 || containerRect.width === 0) {
        return null;
      }
      
      return {
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      };
    } catch (e) {
      console.warn('[MobileBottomNav] Measurement error:', e);
      return null;
    }
  }, [navItems.length]);

  // Handle nav click - set flag and navigate
  const handleNavClick = useCallback((path: string, customHandler?: () => void) => {
    shouldAnimateNext.current = true;
    
    if (customHandler) {
      customHandler();
      return;
    }
    navigate(path);
    if (!mainTabPaths.includes(path)) {
      window.scrollTo(0, 0);
    }
  }, [navigate, mainTabPaths]);

  // Update indicator when activeIndex changes
  useEffect(() => {
    // Clear any pending animation
    if (animationTimeoutRef.current) {
      window.clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    if (activeIndex < 0) {
      setIndicatorStyle(prev => ({ ...prev, opacity: 0 }));
      prevActiveIndex.current = activeIndex;
      return;
    }

    const targetMeasure = measureButton(activeIndex);
    if (!targetMeasure) return;

    const prevIndex = prevActiveIndex.current;
    const shouldAnimate = shouldAnimateNext.current && prevIndex >= 0 && prevIndex !== activeIndex;

    // Reset the flag immediately
    shouldAnimateNext.current = false;

    // If not animating, just snap
    if (!shouldAnimate) {
      setIndicatorStyle({
        left: targetMeasure.left,
        width: targetMeasure.width,
        opacity: 1,
      });
      prevActiveIndex.current = activeIndex;
      initialized.current = true;
      return;
    }

    const prevMeasure = measureButton(prevIndex);
    if (!prevMeasure) {
      setIndicatorStyle({
        left: targetMeasure.left,
        width: targetMeasure.width,
        opacity: 1,
      });
      prevActiveIndex.current = activeIndex;
      return;
    }

    // Animate: direction-aware stretch then shrink
    const isMovingRight = activeIndex > prevIndex;
    setIsAnimating(true);

    if (isMovingRight) {
      // Phase 1: stretch to new right edge
      setIndicatorStyle({
        left: prevMeasure.left,
        width: (targetMeasure.left + targetMeasure.width) - prevMeasure.left,
        opacity: 1,
      });

      // Phase 2: shrink to target
      animationTimeoutRef.current = window.setTimeout(() => {
        setIndicatorStyle({
          left: targetMeasure.left,
          width: targetMeasure.width,
          opacity: 1,
        });
        setIsAnimating(false);
        animationTimeoutRef.current = null;
      }, 150);
    } else {
      // Phase 1: stretch to new left edge
      const currentRight = prevMeasure.left + prevMeasure.width;
      setIndicatorStyle({
        left: targetMeasure.left,
        width: currentRight - targetMeasure.left,
        opacity: 1,
      });

      // Phase 2: shrink to target
      animationTimeoutRef.current = window.setTimeout(() => {
        setIndicatorStyle({
          left: targetMeasure.left,
          width: targetMeasure.width,
          opacity: 1,
        });
        setIsAnimating(false);
        animationTimeoutRef.current = null;
      }, 150);
    }

    prevActiveIndex.current = activeIndex;

    return () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [activeIndex, measureButton]);

  // Initialize on mount + handle resize using ResizeObserver
  useEffect(() => {
    const container = navContainerRef.current;
    if (!container) return;

    const snapToActive = () => {
      if (activeIndex >= 0 && !isAnimating) {
        const measure = measureButton(activeIndex);
        if (measure) {
          setIndicatorStyle({
            left: measure.left,
            width: measure.width,
            opacity: 1,
          });
          initialized.current = true;
        }
      }
    };

    // ResizeObserver for reliable layout detection
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          snapToActive();
        }
      }
    });

    resizeObserver.observe(container);

    // Also handle window resize for orientation changes
    const handleResize = () => snapToActive();
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [activeIndex, measureButton, isAnimating]);

  // Force re-snap when path changes (handles direct navigation to /roxbox)
  useEffect(() => {
    if (activeIndex >= 0) {
      const timer = setTimeout(() => {
        const measure = measureButton(activeIndex);
        if (measure) {
          setIndicatorStyle({
            left: measure.left,
            width: measure.width,
            opacity: 1,
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentPath, activeIndex, measureButton]);

  // Visibility is now handled entirely by inline styles - no recovery hack needed

  // Trial CTA handlers
  const handleAcceptTrial = async () => {
    if (!session?.user) {
      navigate('/userflow');
      return;
    }

    window.dispatchEvent(new CustomEvent('open-tier-selector', {
      detail: {
        onSelect: handleTierSelected
      }
    }));
  };

  const handleTierSelected = async (tier: 'monthly' | 'annual', price: number) => {
    let stripePk: string | null = null;
    let applePayReady = false;

    try {
      const { data: configData } = await supabase.functions.invoke('get-stripe-config');
      if (configData?.publishableKey && isIOS) {
        stripePk = configData.publishableKey;
        applePayReady = await ensureApplePay(
          configData.publishableKey,
          country || 'GB',
          currency.code.toLowerCase()
        );
      }
    } catch (error) {
      console.error('[NAV-TRIAL] Payment init error:', error);
    }

    if (isIOS && applePayReady && stripePk) {
      try {
        const success = await tryApplePay(
          stripePk,
          session!.user.email!,
          tier,
          subscriptionStatus?.has_had_subscription ? (appConfig?.monthly_price || 9.99) : 0,
          currency.symbol,
          subscriptionStatus?.has_had_subscription || false,
          appConfig?.monthly_price || 9.99,
          async () => {
            const { data, error } = await supabase.functions.invoke('create-setup-intent', {
              body: { email: session!.user.email }
            });
            if (error || !data?.client_secret) throw error || new Error('No client secret');
            return data;
          },
          async (paymentMethod: string) => {
            const { error } = await supabase.functions.invoke('start-trial-subscription', {
              body: { payment_method: paymentMethod, tier }
            });
            if (error) throw error;
          }
        );
        
        if (success) {
          console.log('[NAV-TRIAL] Apple Pay success!');
          await queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
          await queryClient.refetchQueries({ queryKey: ['subscriptionStatus'] });
          return;
        }
      } catch (error) {
        console.error('[NAV-TRIAL] Apple Pay error:', error);
      }
    }

    console.log('[NAV-TRIAL] No native payment method, form will be shown by tier selector');
  };

  // Hide nav when Now Playing is open
  if (isNowPlayingOpen) {
    return null;
  }

  // Trial CTA node - hide when scrolled down
  const trialCtaNode = showTrialCTA && !ctaDismissed && !isScrolled ? (
    <div
      className="fixed left-0 right-0 z-[10000] flex justify-center animate-rubberband"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)",
        transform: 'translate3d(0, 0, 0)',
        WebkitTransform: 'translate3d(0, 0, 0)',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        willChange: 'auto',
        pointerEvents: 'auto',
        touchAction: 'none',
        overscrollBehavior: 'contain',
        isolation: 'isolate'
      }}
    >
      <div className="mobile-nav-container w-[92vw] max-w-[420px] h-[60px]">
        <div
          className="mobile-nav-content w-full h-full rounded-full flex items-center justify-between px-4 gap-2 cursor-pointer"
          onClick={handleAcceptTrial}
        >
          <div className="flex-1 basis-auto h-full flex flex-col justify-center text-left overflow-visible opacity-100">
            <span className="text-white font-semibold text-[12px] leading-tight">
              {!session ? 'Get Started' : subscriptionStatus?.has_had_subscription ? 'Renew Your Subscription' : 'Subscribe Now'}
            </span>
            {session && (
              <span className="text-white/80 text-[10px] leading-snug">
                {subscriptionStatus?.has_had_subscription
                  ? `Continue with ${currency.symbol}${appConfig?.monthly_price || 7.99}/month`
                  : `From ${currency.symbol}${((appConfig?.annual_price || 47.88) / 12).toFixed(2)}/month, billed annually`}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              variant="default"
              size="sm"
              className="bg-[hsl(var(--signin-button))] hover:bg-[hsl(var(--signin-button))]/90 text-white font-semibold whitespace-nowrap text-[12px] px-4 py-2 h-9 pointer-events-none"
            >
              {subscriptionStatus?.has_had_subscription ? 'Renew' : 'Subscribe'}
            </Button>

            <Button
              onClick={(e) => {
                e.stopPropagation();
                setCtaDismissed(true);
                setShowTrialCTA(false);
              }}
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 h-7 w-7 flex-shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
  return (
    <>
      {trialCtaNode}

      <div
        ref={navRef}
        className="fixed left-0 right-0 z-[9999] flex justify-center"
        style={{ 
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)",
          transform: 'translate3d(0, 0, 0)',
          WebkitTransform: 'translate3d(0, 0, 0)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          willChange: 'auto',
          pointerEvents: showTrialCTA && !ctaDismissed && !isScrolled ? 'none' : 'auto',
          opacity: showTrialCTA && !ctaDismissed && !isScrolled ? 0 : 1,
          transition: 'opacity 160ms ease-out',
          isolation: 'isolate'
        }}
      >
        <div 
          className="mobile-nav-container w-full mx-0 my-[5px]"
          style={{ touchAction: 'none' }}
        >
          <nav 
            ref={navContainerRef}
            className="mobile-nav-content w-full py-2 rounded-full flex items-center justify-around z-20 relative my-0"
            style={{ touchAction: 'manipulation' }}
          >
            {/* Sliding background indicator */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 h-[44px] rounded-full bg-white/10"
              style={{
                left: indicatorStyle.left,
                width: indicatorStyle.width,
                opacity: indicatorStyle.opacity,
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-out',
              }}
            />
            
            {navItems.map(({ path, label, icon: Icon, onClick }, index) => {
              const isActive = currentPath === path;
              return (
                <button
                  key={path}
                  ref={(el) => { buttonRefs.current[index] = el; }}
                  onPointerDown={() => playClickSound()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') playClickSound();
                  }}
                  onClick={() => handleNavClick(path, onClick)}
                  className="relative z-10 flex items-center justify-center gap-2 py-[15px] px-5 rounded-full"
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}
                  type="button"
                  aria-label={label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon 
                    size={20} 
                    className={`transition-colors duration-200 ${isActive ? 'text-white' : 'text-white/70'}`} 
                  />
                  <span 
                    className={`text-xs font-medium font-sans whitespace-nowrap ${
                      isActive ? 'text-zinc-50 ml-1' : 'hidden'
                    }`}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
      />
    </>
  );
};

// Memoize to prevent re-renders from parent state changes
export const MobileBottomNav = memo(MobileBottomNavInner);
